import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Card,
  CardContent,
  useMediaQuery,
  useTheme,
  Stack,
  Collapse,
  Autocomplete,
  InputAdornment,
  Switch,
  FormControlLabel,
  Tooltip
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import {
  ArrowBack as BackIcon,
  PictureAsPdf as PdfIcon,
  VisibilityOutlined as ViewPdfIcon,
  LockOpen as LockOpenIcon,
  LockOutlined as LockOutlinedIcon,
  Refresh as RefreshIcon,
  Send as SendIcon,
  CheckCircle as AcceptIcon,
  Cancel as RejectIcon,
  Assignment as WorkOrderIcon,
  Receipt as InvoiceIcon,
  Email as EmailIcon,
  Edit as EditIcon,
  Lock as FinalizeIcon,
  Add as AddIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { can } from '../../utils/roleUtils';
import ApiService from '../../services/api.service';
import internalServiceService from '../../services/internal-service.service';
import { getBrandColor, getBrandContrastText, getBrandHover, getBrandHoverTint, getBrandInk } from '../../utils/colorUtils';

// Tax Configuration - UAE VAT Rate (can be changed for different countries)
const VAT_RATE = 0.05; // 5% UAE VAT
const VAT_RATE_DISPLAY = `${VAT_RATE * 100}%`; // "5%"

const QuotationDetail = ({ currentUser }) => {
  const navigate = useNavigate();
  const { quotationId } = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [quotation, setQuotation] = useState(null);
  const [expandedItems, setExpandedItems] = useState({});

  // Dialog states
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [emailRecipients, setEmailRecipients] = useState([]);
  const [convertTo, setConvertTo] = useState(''); // 'workorder' or 'invoice'

  // Action loading states
  const [sending, setSending] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [converting, setConverting] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [finalizing, setFinalizing] = useState(false);

  // Add Service dialog state
  const [addServiceDialogOpen, setAddServiceDialogOpen] = useState(false);
  const [availableServices, setAvailableServices] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [isAddingCustomService, setIsAddingCustomService] = useState(false);
  const [customServiceName, setCustomServiceName] = useState('');
  const [customServiceCharge, setCustomServiceCharge] = useState('');
  const [customGovtCharge, setCustomGovtCharge] = useState('');
  const [addMarkup, setAddMarkup] = useState('');
  const [addingLineItem, setAddingLineItem] = useState(false);

  // Line-item markup apply/edit/remove
  const [markupDialogOpen, setMarkupDialogOpen] = useState(false);
  const [selectedLineItem, setSelectedLineItem] = useState(null);
  const [lineItemMarkupAmount, setLineItemMarkupAmount] = useState('');
  const [applyingLineItemMarkup, setApplyingLineItemMarkup] = useState(false);

  // FINAL edit gate (frontend-only, mirrors the invoice) + tax-details toggle + PDF view
  const [finalEditMode, setFinalEditMode] = useState(false);
  const [reopenDialogOpen, setReopenDialogOpen] = useState(false);
  const [includeTaxDetails, setIncludeTaxDetails] = useState(true);
  const [viewingPdf, setViewingPdf] = useState(false);

  useEffect(() => {
    fetchQuotation();
  }, [quotationId]);

  const fetchQuotation = async () => {
    setLoading(true);
    try {
      const response = await ApiService.getQuotationById(quotationId);
      const data = response?.data?.data;
      if (data) {
        setQuotation(data);
        // Pre-populate email recipients
        if (data.customer_email) {
          setEmailRecipients([data.customer_email]);
        }
      } else {
        setError('Quotation not found');
      }
    } catch (err) {
      console.error('Error fetching quotation:', err);
      setError('Failed to load quotation');
    } finally {
      setLoading(false);
    }
  };

  const handleFinalizeQuotation = async () => {
    if (!window.confirm('Are you sure you want to finalize this quotation? A permanent quotation number will be assigned.')) {
      return;
    }

    setFinalizing(true);
    try {
      const response = await ApiService.finalizeQuotation(quotationId);
      console.log('Quotation finalized:', response?.data);
      const data = response?.data?.data;
      setSuccess(`Quotation finalized with number: ${data?.quotationNumber || ''}`);
      // Refresh quotation to get updated status and number
      await fetchQuotation();
    } catch (err) {
      console.error('Error finalizing quotation:', err);
      setError(err.response?.data?.message || 'Failed to finalize quotation');
    } finally {
      setFinalizing(false);
    }
  };

  const handleSendQuotation = async () => {
    if (emailRecipients.length === 0) {
      setError('Please add at least one recipient email');
      return;
    }

    setSending(true);
    try {
      const response = await ApiService.sendQuotation(quotationId, { recipients: emailRecipients });
      console.log('Quotation sent:', response?.data);
      setSendDialogOpen(false);
      setSuccess('Quotation sent successfully!');
      // Refresh quotation to get updated status
      await fetchQuotation();
    } catch (err) {
      console.error('Error sending quotation:', err);
      setError(err.response?.data?.message || 'Failed to send quotation');
    } finally {
      setSending(false);
    }
  };

  const handleAcceptQuotation = async () => {
    if (!window.confirm('Are you sure you want to mark this quotation as accepted?')) {
      return;
    }

    setAccepting(true);
    try {
      const response = await ApiService.acceptQuotation(quotationId);
      console.log('Quotation accepted:', response?.data);
      setSuccess('Quotation marked as accepted!');
      // Refresh quotation to get updated status
      await fetchQuotation();
    } catch (err) {
      console.error('Error accepting quotation:', err);
      setError(err.response?.data?.message || 'Failed to accept quotation');
    } finally {
      setAccepting(false);
    }
  };

  const handleRejectQuotation = async () => {
    if (!rejectReason.trim()) {
      setError('Please provide a reason for rejection');
      return;
    }

    setRejecting(true);
    try {
      const response = await ApiService.rejectQuotation(quotationId, { reason: rejectReason });
      console.log('Quotation rejected:', response?.data);
      setRejectDialogOpen(false);
      setRejectReason('');
      setSuccess('Quotation marked as rejected');
      // Refresh quotation to get updated status
      await fetchQuotation();
    } catch (err) {
      console.error('Error rejecting quotation:', err);
      setError(err.response?.data?.message || 'Failed to reject quotation');
    } finally {
      setRejecting(false);
    }
  };

  const handleConvert = async () => {
    setConverting(true);
    try {
      if (convertTo === 'workorder') {
        // Create work order from quotation
        const response = await ApiService.createWorkOrderFromQuotation(quotationId);
        console.log('Work order created from quotation:', response?.data);
        const workOrder = response?.data?.data;
        setSuccess(`Work order ${workOrder?.workOrderNumber || ''} created successfully!`);
        setConvertDialogOpen(false);
        // Navigate to the created work order
        navigate(`/work-orders/${workOrder?.id}`);
        return;
      }
      // TODO: Direct Quote→Invoice conversion not yet supported.
      // Convert the quotation to a Work Order first, then invoice the WO.
      setConvertDialogOpen(false);
      await fetchQuotation();
    } catch (err) {
      console.error('Error converting quotation:', err);
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to convert quotation');
    } finally {
      setConverting(false);
    }
  };

  const pdfParams = () => (includeTaxDetails ? {} : { includeTaxDetails: false });

  const handleGeneratePdf = async () => {
    setGeneratingPdf(true);
    try {
      const response = await ApiService.getQuotationPdfView(quotationId, pdfParams());
      const blobUrl = window.URL.createObjectURL(response.data);
      const fileName = `Quotation-${quotation?.quotation_number || quotation?.draft_number || quotationId}.pdf`;
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => window.URL.revokeObjectURL(blobUrl), 30000);
    } catch (err) {
      console.error('Error generating PDF:', err);
      setError(err.response?.data?.message || 'Failed to generate PDF');
    } finally {
      setGeneratingPdf(false);
    }
  };

  const handleViewPdf = async () => {
    setViewingPdf(true);
    try {
      const response = await ApiService.getQuotationPdfView(quotationId, pdfParams());
      const blobUrl = window.URL.createObjectURL(response.data);
      window.open(blobUrl, '_blank', 'noopener,noreferrer');
      window.setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
    } catch (err) {
      console.error('Error opening PDF:', err);
      setError(err.response?.data?.message || 'Failed to open PDF');
    } finally {
      setViewingPdf(false);
    }
  };

  const handleAddRecipient = () => {
    const email = prompt('Enter email address:');
    if (email && !emailRecipients.includes(email)) {
      setEmailRecipients(prev => [...prev, email]);
    }
  };

  const handleRemoveRecipient = (email) => {
    setEmailRecipients(prev => prev.filter(e => e !== email));
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      DRAFT: { color: 'warning', label: 'Draft' },
      FINAL: { color: 'primary', label: 'Final' },
      SENT: { color: 'info', label: 'Sent' },
      ACCEPTED: { color: 'success', label: 'Accepted' },
      REJECTED: { color: 'error', label: 'Rejected' },
      EXPIRED: { color: 'error', label: 'Expired', variant: 'outlined' },
      CONVERTED: { color: 'secondary', label: 'Converted' }
    };
    const config = statusConfig[status] || { color: 'info', label: status, variant: 'outlined' };
    return <Chip label={config.label} color={config.color} variant={config.variant} />;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED'
    }).format(amount || 0);
  };

  // Load services for Add Service dialog — internal-service prices resolved for the quotation's org.
  const loadServices = async () => {
    try {
      const orgId = quotation?.organization_id || quotation?.organizationId || null;
      const allServices = await internalServiceService.getServicesForLineItems(orgId);
      const services = allServices.map(s => ({
        id: s.id,
        name: s.name || s.service_name,
        description: s.description || '',
        service_charge: s.service_charge || s.serviceCharge || 0,
        tax_on_service_charge: s.tax_on_service_charge || s.taxOnServiceCharge || 0,
        govt_charge: s.govt_charge || s.govtCharge || 0,
        type: s.type || 'THAKLIS'
      }));
      setAvailableServices(services);
    } catch (err) {
      console.error('Error loading services:', err);
      setAvailableServices([]);
    }
  };

  const openAddServiceDialog = () => {
    loadServices();
    setSelectedService(null);
    setIsAddingCustomService(false);
    setCustomServiceName('');
    setCustomServiceCharge('');
    setCustomGovtCharge('');
    setAddMarkup('');
    setAddServiceDialogOpen(true);
  };

  const handleAddLineItem = async () => {
    const markupInclVat = parseFloat(addMarkup) || 0;
    const markupBase = Number((markupInclVat / 1.05).toFixed(2));
    const markupVat = Number((markupInclVat - markupBase).toFixed(2));

    let serviceCharge, govtCharge, serviceTax, serviceName, serviceDescription, serviceId, isCustom;

    if (isAddingCustomService) {
      if (!customServiceName || !customServiceCharge) {
        setError('Please enter service name and charge');
        return;
      }
      const inclVat = parseFloat(customServiceCharge) || 0;
      serviceCharge = Number((inclVat / 1.05).toFixed(2));
      serviceTax = Number((inclVat - serviceCharge).toFixed(2));
      govtCharge = parseFloat(customGovtCharge) || 0;
      serviceName = customServiceName;
      serviceDescription = '';
      serviceId = null;
      isCustom = true;
    } else {
      if (!selectedService) {
        setError('Please select a service');
        return;
      }
      serviceCharge = selectedService.service_charge || 0;
      serviceTax = selectedService.tax_on_service_charge || 0;
      govtCharge = selectedService.govt_charge || 0;
      serviceName = selectedService.name;
      serviceDescription = selectedService.description || '';
      serviceId = selectedService.id;
      isCustom = selectedService.type === 'INTERNAL';
    }

    const lineTotal = serviceCharge + govtCharge + markupBase + serviceTax + markupVat;
    const originalLineTotal = serviceCharge + govtCharge + serviceTax;

    const itemData = {
      service_id: serviceId,
      service_name: serviceName,
      service_description: serviceDescription,
      quantity: 1,
      unit_price: serviceCharge + govtCharge,
      service_charge: serviceCharge,
      govt_charge: govtCharge,
      tax_value: serviceTax,
      tax_on_markup: markupVat,
      line_total: lineTotal,
      original_line_total: originalLineTotal,
      markup_amount: markupBase,
      is_custom_service: isCustom
    };

    setAddingLineItem(true);
    try {
      await ApiService.addQuotationLineItem(quotationId, itemData);
      setAddServiceDialogOpen(false);
      setSuccess('Service added');
      fetchQuotation();
    } catch (err) {
      console.error('Error adding line item:', err);
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to add service');
    } finally {
      setAddingLineItem(false);
    }
  };

  const handleRemoveLineItem = async (lineItemId) => {
    if (!window.confirm('Are you sure you want to remove this service from the quotation?')) return;
    try {
      await ApiService.removeQuotationLineItem(quotationId, lineItemId);
      setSuccess('Service removed');
      fetchQuotation();
    } catch (err) {
      console.error('Error removing line item:', err);
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to remove service');
    }
  };

  // Line item markup handlers — mirrors the invoice detail page.
  // Input is markup-including-tax; split via VAT_RATE (5%).
  const openLineItemMarkupDialog = (lineItem) => {
    setSelectedLineItem(lineItem);
    const existingInclTax = (parseFloat(lineItem.markup_amount) || 0)
      + (parseFloat(lineItem.tax_on_markup) || 0);
    setLineItemMarkupAmount(existingInclTax > 0 ? String(existingInclTax) : '');
    setMarkupDialogOpen(true);
  };

  const closeLineItemMarkupDialog = () => {
    setMarkupDialogOpen(false);
    setSelectedLineItem(null);
    setLineItemMarkupAmount('');
  };

  const handleApplyLineItemMarkup = async () => {
    if (!selectedLineItem || !lineItemMarkupAmount || parseFloat(lineItemMarkupAmount) <= 0) {
      setError('Please enter a valid markup amount');
      return;
    }
    const markupInclTax = parseFloat(lineItemMarkupAmount);
    const markupExclTax = Number((markupInclTax / (1 + VAT_RATE)).toFixed(2));
    const taxOnMarkup = Number((markupInclTax - markupExclTax).toFixed(2));

    setApplyingLineItemMarkup(true);
    try {
      await ApiService.applyQuotationLineItemMarkup(selectedLineItem.id, {
        markupAmount: markupExclTax,
        taxOnMarkup
      });
      setSuccess('Markup applied');
      closeLineItemMarkupDialog();
      fetchQuotation();
    } catch (err) {
      console.error('Error applying markup:', err);
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to apply markup');
    } finally {
      setApplyingLineItemMarkup(false);
    }
  };

  const handleRemoveLineItemMarkup = async (lineItemId) => {
    if (!window.confirm('Remove markup from this service?')) return;
    try {
      await ApiService.removeQuotationLineItemMarkup(lineItemId);
      setSuccess('Markup removed');
      fetchQuotation();
    } catch (err) {
      console.error('Error removing markup:', err);
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to remove markup');
    }
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatDateTime = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const toggleExpand = (itemId) => {
    setExpandedItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!quotation) {
    return (
      <Box>
        <Alert severity="error">Quotation not found</Alert>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/quotations')} sx={{ mt: 2 }}>
          Back to Quotations
        </Button>
      </Box>
    );
  }

  const isDraft = quotation.status === 'DRAFT';
  const isFinal = quotation.status === 'FINAL';
  const isSent = quotation.status === 'SENT';
  const isAccepted = quotation.status === 'ACCEPTED';
  const isRejected = quotation.status === 'REJECTED';
  // Write actions follow the permission matrix (TC_ADMIN/platform resolve all-true).
  const canWriteQ = can(currentUser, 'quotation', 'write');
  const canCreateWorkOrder = canWriteQ && can(currentUser, 'work_order', 'write');

  // Editable in DRAFT and FINAL (mirrors the invoice). FINAL line-item edits are gated
  // behind a frontend-only "Reopen for editing" toggle; the backend allows DRAFT + FINAL.
  const isEditable = isDraft || isFinal;
  const lineItemsUnlocked = (isDraft || (isFinal && finalEditMode)) && canWriteQ;

  const quotationNumber = quotation.quotation_number || quotation.draft_number || '-';

  return (
    <Box sx={{ pb: isMobile ? 2 : 0 }}>
      {/* Header */}
      <Box
        display="flex"
        flexDirection={isSmallMobile ? 'column' : 'row'}
        justifyContent="space-between"
        alignItems={isSmallMobile ? 'flex-start' : 'center'}
        mb={3}
        gap={isSmallMobile ? 2 : 0}
      >
        <Box display="flex" alignItems="center">
          <IconButton onClick={() => navigate('/quotations')} sx={{ mr: 1 }}>
            <BackIcon />
          </IconButton>
          <Box>
            <Typography variant={isSmallMobile ? 'h6' : 'h5'} fontWeight={600}>
              {quotationNumber}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {quotation.organization_name}
            </Typography>
          </Box>
        </Box>
        <Box display="flex" alignItems="center" gap={1} sx={{ ml: isSmallMobile ? 5 : 0 }}>
          {getStatusChip(quotation.status)}
          <IconButton onClick={fetchQuotation} size={isSmallMobile ? 'small' : 'medium'}>
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Grid container spacing={isMobile ? 2 : 3}>
        {/* Quotation Info */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: isMobile ? 2 : 3 }}>
            <Typography variant="h6" fontWeight={600} mb={2}>
              Quotation Details
            </Typography>

            <Box mb={1.5}>
              <Typography variant="caption" color="text.secondary">Quotation Number</Typography>
              <Typography variant="body2" fontWeight={600}>{quotationNumber}</Typography>
            </Box>

            <Box mb={1.5}>
              <Typography variant="caption" color="text.secondary">Customer Organization</Typography>
              <Typography variant="body2">{quotation.organization_name}</Typography>
              {quotation.organization_code && (
                <Typography variant="caption" color="text.secondary">
                  Code: {quotation.organization_code}
                </Typography>
              )}
            </Box>

            <Box mb={1.5}>
              <Typography variant="caption" color="text.secondary">Contact</Typography>
              <Typography variant="body2">{quotation.customer_name || '-'}</Typography>
              <Typography variant="caption" color="text.secondary">
                {quotation.customer_email}
              </Typography>
            </Box>

            <Box mb={1.5}>
              <Typography variant="caption" color="text.secondary">Quotation Date</Typography>
              <Typography variant="body2">{formatDate(quotation.quotation_date)}</Typography>
            </Box>

            <Box mb={1.5}>
              <Typography variant="caption" color="text.secondary">Valid Until</Typography>
              <Typography variant="body2">{formatDate(quotation.valid_until)}</Typography>
            </Box>

            {quotation.sent_at && (
              <Box mb={1.5}>
                <Typography variant="caption" color="text.secondary">Sent On</Typography>
                <Typography variant="body2">{formatDateTime(quotation.sent_at)}</Typography>
              </Box>
            )}

            {quotation.accepted_at && (
              <Box mb={1.5}>
                <Typography variant="caption" color="text.secondary">Accepted On</Typography>
                <Typography variant="body2" color="success.main">{formatDateTime(quotation.accepted_at)}</Typography>
              </Box>
            )}

            {quotation.rejected_at && (
              <Box mb={1.5}>
                <Typography variant="caption" color="text.secondary">Rejected On</Typography>
                <Typography variant="body2" color="error.main">{formatDateTime(quotation.rejected_at)}</Typography>
              </Box>
            )}

            <Divider sx={{ my: 2 }} />

            {/* Financial Summary */}
            <Typography variant="body2" fontWeight={600} mb={1}>Summary</Typography>

            <Box display="flex" justifyContent="space-between" mb={0.5}>
              <Typography variant="body2">Items</Typography>
              <Typography variant="body2" fontWeight={600}>{quotation.item_count}</Typography>
            </Box>

            <Box display="flex" justifyContent="space-between" mb={0.5}>
              <Typography variant="body2">Subtotal</Typography>
              <Typography variant="body2">{formatCurrency(quotation.subtotal)}</Typography>
            </Box>

            <Box display="flex" justifyContent="space-between" mb={0.5}>
              <Typography variant="body2">Tax</Typography>
              <Typography variant="body2">{formatCurrency(quotation.tax_total)}</Typography>
            </Box>

            <Divider sx={{ my: 1 }} />

            <Box display="flex" justifyContent="space-between">
              <Typography variant="body2" fontWeight={600}>Grand Total</Typography>
              <Typography variant="body2" fontWeight={600} color="primary">
                {formatCurrency(quotation.grand_total)}
              </Typography>
            </Box>
          </Paper>

          {/* Actions */}
          <Paper sx={{ p: 3, mt: 3 }}>
            <Typography variant="h6" fontWeight={600} mb={2}>Actions</Typography>

            <Box display="flex" flexDirection="column" gap={1}>
              {isDraft && canWriteQ && (
                <>
                  <Button
                    variant="contained"
                    startIcon={finalizing ? <CircularProgress size={20} /> : <FinalizeIcon />}
                    onClick={handleFinalizeQuotation}
                    disabled={finalizing}
                    fullWidth
                    sx={{
                      backgroundColor: '#059669',
                      color: '#fff',
                      '&:hover': { backgroundColor: '#047857' },
                      '&:disabled': { backgroundColor: '#d1d5db', color: '#9ca3af' }
                    }}
                  >
                    {finalizing ? 'Finalizing...' : 'Finalize Quotation'}
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<SendIcon />}
                    onClick={() => setSendDialogOpen(true)}
                    fullWidth
                  >
                    Send to Customer
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={() => navigate(`/quotations/${quotationId}/edit`)}
                    fullWidth
                    sx={{
                      borderColor: 'primary.main',
                      color: 'secondary.main',
                      '&:hover': { borderColor: 'primary.dark', bgcolor: 'action.hover' }
                    }}
                  >
                    Edit Quotation
                  </Button>
                </>
              )}

              {isFinal && canWriteQ && (
                <>
                  <Button
                    variant="contained"
                    startIcon={<SendIcon />}
                    onClick={() => setSendDialogOpen(true)}
                    fullWidth
                  >
                    Send to Customer
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={() => navigate(`/quotations/${quotationId}/edit`)}
                    fullWidth
                    sx={{
                      borderColor: 'primary.main',
                      color: 'secondary.main',
                      '&:hover': { borderColor: 'primary.dark', bgcolor: 'action.hover' }
                    }}
                  >
                    Edit Quotation
                  </Button>
                  {canCreateWorkOrder && (
                  <Button
                    variant="outlined"
                    startIcon={<WorkOrderIcon />}
                    onClick={() => {
                      setConvertTo('workorder');
                      setConvertDialogOpen(true);
                    }}
                    fullWidth
                    sx={{
                      borderColor: getBrandInk(theme),
                      color: getBrandInk(theme),
                      '&:hover': {
                        borderColor: getBrandInk(theme),
                        backgroundColor: getBrandHoverTint(theme, 0.08)
                      }
                    }}
                  >
                    Create Work Order
                  </Button>
                  )}
                </>
              )}

              {isSent && canWriteQ && (
                <>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={accepting ? <CircularProgress size={20} /> : <AcceptIcon />}
                    onClick={handleAcceptQuotation}
                    disabled={accepting}
                    fullWidth
                  >
                    {accepting ? 'Processing...' : 'Mark as Accepted'}
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<RejectIcon />}
                    onClick={() => setRejectDialogOpen(true)}
                    fullWidth
                  >
                    Mark as Rejected
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<SendIcon />}
                    onClick={() => setSendDialogOpen(true)}
                    fullWidth
                  >
                    Resend
                  </Button>
                </>
              )}

              {isAccepted && canCreateWorkOrder && (
                <>
                  <Button
                    variant="contained"
                    startIcon={<WorkOrderIcon />}
                    onClick={() => {
                      setConvertTo('workorder');
                      setConvertDialogOpen(true);
                    }}
                    fullWidth
                    sx={{
                      backgroundColor: getBrandColor(theme),
                      color: getBrandContrastText(theme),
                      '&:hover': { backgroundColor: getBrandHover(theme) }
                    }}
                  >
                    Create Work Order
                  </Button>
                  {/*
                    TODO: Direct Quote→Invoice conversion is not yet supported
                    on the backend. Convert the quotation to a Work Order first,
                    then create an invoice from the WO. Uncomment when the
                    backend endpoint POST /api/invoices/from-quotation/{id}
                    is implemented.

                  <Button
                    variant="outlined"
                    startIcon={<InvoiceIcon />}
                    onClick={() => {
                      setConvertTo('invoice');
                      setConvertDialogOpen(true);
                    }}
                    fullWidth
                    sx={{
                      borderColor: '#1a237e',
                      color: '#1a237e',
                      '&:hover': { borderColor: '#0d1642', backgroundColor: 'rgba(26, 35, 126, 0.04)' }
                    }}
                  >
                    Create Invoice Directly
                  </Button>
                  */}
                </>
              )}

              {!isDraft && (
                <>
                  <Button
                    variant="outlined"
                    startIcon={viewingPdf ? <CircularProgress size={20} /> : <ViewPdfIcon />}
                    onClick={handleViewPdf}
                    disabled={viewingPdf}
                    fullWidth
                  >
                    {viewingPdf ? 'Opening...' : 'View PDF'}
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={generatingPdf ? <CircularProgress size={20} /> : <PdfIcon />}
                    onClick={handleGeneratePdf}
                    disabled={generatingPdf}
                    fullWidth
                  >
                    {generatingPdf ? 'Generating...' : 'Download PDF'}
                  </Button>
                </>
              )}

              {isRejected && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  This quotation was rejected.
                  {quotation.internal_notes && (
                    <Typography variant="body2">Reason: {quotation.internal_notes}</Typography>
                  )}
                </Alert>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Line Items */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: isMobile ? 2 : 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
              <Typography variant="h6" fontWeight={600}>
                Line Items ({quotation.lineItems?.length || 0})
              </Typography>
              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" justifyContent="flex-end">
                <FormControlLabel
                  control={
                    <Switch
                      checked={includeTaxDetails}
                      onChange={(e) => setIncludeTaxDetails(e.target.checked)}
                      size="small"
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                      Tax Details
                    </Typography>
                  }
                  labelPlacement="start"
                  sx={{ mr: 1 }}
                />
                {lineItemsUnlocked && (
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={openAddServiceDialog}
                  >
                    Add Service
                  </Button>
                )}
              </Box>
            </Box>

            {/* FINAL edit gate: line items are locked until the user opts in (frontend only). */}
            {isFinal && (
              <Alert
                severity={finalEditMode ? 'warning' : 'info'}
                icon={finalEditMode ? <LockOpenIcon fontSize="small" /> : <LockOutlinedIcon fontSize="small" />}
                action={
                  finalEditMode ? (
                    <Button size="small" color="inherit" startIcon={<FinalizeIcon fontSize="small" />}
                      onClick={() => setFinalEditMode(false)}>
                      Lock quotation
                    </Button>
                  ) : (
                    <Button size="small" color="inherit" startIcon={<LockOpenIcon fontSize="small" />}
                      onClick={() => setReopenDialogOpen(true)}>
                      Reopen for editing
                    </Button>
                  )
                }
                sx={{ mb: 2, '& .MuiAlert-message': { display: 'flex', alignItems: 'center' } }}
              >
                {finalEditMode
                  ? 'Editing a finalized quotation — your changes update a finalized document.'
                  : 'This quotation is FINAL. Line items are locked.'}
              </Alert>
            )}

            {/* Mobile Card View */}
            {isMobile ? (
              <Stack spacing={2}>
                {(!quotation.lineItems || quotation.lineItems.length === 0) ? (
                  <Box sx={{ py: 4, textAlign: 'center' }}>
                    <Typography color="text.secondary">No line items</Typography>
                  </Box>
                ) : (
                  quotation.lineItems.map((item) => {
                    const isExpanded = expandedItems[item.id];
                    return (
                      <Card key={item.id} variant="outlined">
                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                            <Box flex={1}>
                              <Typography fontWeight={600} sx={{ fontSize: '0.9rem' }}>
                                {item.service_name}
                              </Typography>
                              <Typography color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                                Qty: {item.quantity}
                              </Typography>
                            </Box>
                            <Typography fontWeight={700} color="primary" sx={{ fontSize: '1rem' }}>
                              {formatCurrency(item.line_total)}
                            </Typography>
                          </Box>

                          <Box
                            onClick={() => toggleExpand(item.id)}
                            sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', color: 'secondary.main' }}
                          >
                            <Typography sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
                              {isExpanded ? 'Hide Details' : 'Show Details'}
                            </Typography>
                            {isExpanded ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
                          </Box>

                          <Collapse in={isExpanded}>
                            <Divider sx={{ my: 1 }} />
                            <Grid container spacing={1}>
                              {includeTaxDetails ? (
                                <>
                                  <Grid item xs={6}>
                                    <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>Service Chg</Typography>
                                    <Typography sx={{ fontSize: '0.8rem' }}>{formatCurrency(item.service_charge)}</Typography>
                                  </Grid>
                                  <Grid item xs={6}>
                                    <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>Govt Chg</Typography>
                                    <Typography sx={{ fontSize: '0.8rem' }}>{formatCurrency(item.govt_charge)}</Typography>
                                  </Grid>
                                  <Grid item xs={6}>
                                    <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>Tax ({VAT_RATE_DISPLAY})</Typography>
                                    <Typography sx={{ fontSize: '0.8rem' }}>{formatCurrency(item.tax_value)}</Typography>
                                  </Grid>
                                  {item.markup_amount > 0 && (
                                    <Grid item xs={6}>
                                      <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>Markup</Typography>
                                      <Typography sx={{ fontSize: '0.8rem', color: 'success.main' }}>
                                        +{formatCurrency(item.markup_amount)}
                                      </Typography>
                                    </Grid>
                                  )}
                                </>
                              ) : (
                                <Grid item xs={6}>
                                  <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>Fees/Rate</Typography>
                                  <Typography sx={{ fontSize: '0.8rem' }}>{formatCurrency(item.line_total)}</Typography>
                                </Grid>
                              )}
                            </Grid>

                            {/* Markup + Remove actions */}
                            {lineItemsUnlocked && (
                              <Box display="flex" justifyContent="flex-end" gap={1} mt={2}>
                                {parseFloat(item.markup_amount) > 0 ? (
                                  <>
                                    <Button size="small" variant="outlined" onClick={() => openLineItemMarkupDialog(item)}>
                                      Edit Markup
                                    </Button>
                                    <Button size="small" variant="outlined" color="error" onClick={() => handleRemoveLineItemMarkup(item.id)}>
                                      Remove Markup
                                    </Button>
                                  </>
                                ) : (
                                  <Button size="small" variant="outlined" color="success" onClick={() => openLineItemMarkupDialog(item)}>
                                    Add Markup
                                  </Button>
                                )}
                                <IconButton size="small" color="error" onClick={() => handleRemoveLineItem(item.id)}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            )}
                          </Collapse>
                        </CardContent>
                      </Card>
                    );
                  })
                )}
              </Stack>
            ) : (
              /* Desktop Table View */
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ '& th': { bgcolor: 'action.hover', fontWeight: 600 } }}>
                      <TableCell>Service</TableCell>
                      {includeTaxDetails ? (
                        <>
                          <TableCell align="right">Service Chg</TableCell>
                          <TableCell align="right">Govt Chg</TableCell>
                          <TableCell align="right">Markup</TableCell>
                          <TableCell align="right">Tax</TableCell>
                        </>
                      ) : (
                        <TableCell align="right">Fees/Rate</TableCell>
                      )}
                      <TableCell align="right">Total</TableCell>
                      {lineItemsUnlocked && <TableCell align="center">Action</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(!quotation.lineItems || quotation.lineItems.length === 0) ? (
                      <TableRow>
                        <TableCell
                          colSpan={(includeTaxDetails ? 6 : 3) + (lineItemsUnlocked ? 1 : 0)}
                          align="center"
                          sx={{ py: 4 }}
                        >
                          <Typography color="text.secondary">No line items</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      quotation.lineItems.map((item) => {
                        const itemMarkup = parseFloat(item.markup_amount || 0);
                        const itemTaxOnMarkup = parseFloat(item.tax_on_markup || 0);
                        return (
                        <TableRow key={item.id} hover>
                          <TableCell>
                            <Typography fontWeight={500}>{item.service_name}</Typography>
                            {item.service_description && (
                              <Typography variant="caption" color="text.secondary">
                                {item.service_description}
                              </Typography>
                            )}
                            {item.is_custom_service && !item.service_id && (
                              <Chip label="Custom" size="small" sx={{ ml: 1, height: 16, fontSize: '0.65rem' }} />
                            )}
                          </TableCell>
                          {includeTaxDetails ? (
                            <>
                              <TableCell align="right">{formatCurrency(item.service_charge)}</TableCell>
                              <TableCell align="right">{formatCurrency(item.govt_charge)}</TableCell>
                              <TableCell align="right">
                                <Box display="flex" alignItems="center" justifyContent="flex-end" gap={0.5}>
                                  {itemMarkup > 0 ? (
                                    <Typography sx={{ color: 'success.main', fontWeight: 500 }}>
                                      {formatCurrency(itemMarkup)}
                                    </Typography>
                                  ) : (
                                    <Typography sx={{ color: 'text.disabled' }}>—</Typography>
                                  )}
                                  {lineItemsUnlocked && (
                                    itemMarkup > 0 ? (
                                      <>
                                        <Tooltip title="Edit markup">
                                          <IconButton size="small" onClick={() => openLineItemMarkupDialog(item)}>
                                            <EditIcon sx={{ fontSize: 16 }} />
                                          </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Remove markup">
                                          <IconButton size="small" color="error" onClick={() => handleRemoveLineItemMarkup(item.id)}>
                                            <DeleteIcon sx={{ fontSize: 16 }} />
                                          </IconButton>
                                        </Tooltip>
                                      </>
                                    ) : (
                                      <Tooltip title="Add markup">
                                        <IconButton size="small" color="success" onClick={() => openLineItemMarkupDialog(item)}>
                                          <AddIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                      </Tooltip>
                                    )
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell align="right">
                                <Typography>{formatCurrency(item.tax_value)}</Typography>
                                {itemTaxOnMarkup > 0 && (
                                  <Typography sx={{ fontSize: '0.7rem', color: 'success.main' }}>
                                    (+{formatCurrency(itemTaxOnMarkup)})
                                  </Typography>
                                )}
                              </TableCell>
                            </>
                          ) : (
                            <TableCell align="right">{formatCurrency(item.line_total)}</TableCell>
                          )}
                          <TableCell align="right">
                            <Typography fontWeight={600}>{formatCurrency(item.line_total)}</Typography>
                            {itemMarkup > 0 && includeTaxDetails && (
                              <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                                (Base: {formatCurrency(item.original_line_total)})
                              </Typography>
                            )}
                          </TableCell>
                          {lineItemsUnlocked && (
                            <TableCell align="center">
                              <Tooltip title="Remove">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleRemoveLineItem(item.id)}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          )}
                        </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Totals */}
            {quotation.lineItems && quotation.lineItems.length > 0 && (
              <Box sx={{ mt: 2, display: 'flex', justifyContent: isMobile ? 'center' : 'flex-end' }}>
                <Box sx={{ width: isMobile ? '100%' : 250 }}>
                  <Box display="flex" justifyContent="space-between" mb={0.5}>
                    <Typography variant="body2">Subtotal:</Typography>
                    <Typography variant="body2">{formatCurrency(quotation.subtotal)}</Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" mb={0.5}>
                    <Typography variant="body2">Tax:</Typography>
                    <Typography variant="body2">{formatCurrency(quotation.tax_total)}</Typography>
                  </Box>
                  <Divider sx={{ my: 1 }} />
                  <Box display="flex" justifyContent="space-between">
                    <Typography fontWeight={600}>Grand Total:</Typography>
                    <Typography fontWeight={700} color="primary" variant="h6">
                      {formatCurrency(quotation.grand_total)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}
          </Paper>

          {/* Notes & Terms */}
          {(quotation.notes || quotation.terms_and_conditions) && (
            <Paper sx={{ p: 3, mt: 3 }}>
              <Typography variant="h6" fontWeight={600} mb={2}>Notes & Terms</Typography>
              {quotation.notes && (
                <Box mb={2}>
                  <Typography variant="caption" color="text.secondary">Customer Notes</Typography>
                  <Typography variant="body2">{quotation.notes}</Typography>
                </Box>
              )}
              {quotation.terms_and_conditions && (
                <Box>
                  <Typography variant="caption" color="text.secondary">Terms & Conditions</Typography>
                  <Typography variant="body2">{quotation.terms_and_conditions}</Typography>
                </Box>
              )}
            </Paper>
          )}
        </Grid>
      </Grid>

      {/* Send Quotation Dialog */}
      <Dialog open={sendDialogOpen} onClose={() => setSendDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <EmailIcon color="primary" />
            Send Quotation
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ bgcolor: 'action.hover', p: 2, borderRadius: 1, mb: 2 }}>
            <Typography variant="subtitle2">{quotationNumber}</Typography>
            <Typography variant="body2" color="text.secondary">{quotation.organization_name}</Typography>
            <Typography variant="body2" color="primary" fontWeight={600}>
              {formatCurrency(quotation.grand_total)}
            </Typography>
          </Box>

          {/* Organization Email Info */}
          {quotation.customer_email && (
            <Box sx={{ mb: 2, p: 1.5, backgroundColor: '#e8f5e9', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">Organization Email</Typography>
              <Typography variant="body2" fontWeight={500}>{quotation.customer_email}</Typography>
            </Box>
          )}

          <Typography variant="subtitle2" mb={1}>Recipients *</Typography>

          {/* Add email input */}
          <Box display="flex" gap={1} mb={2}>
            <TextField
              size="small"
              placeholder="Enter email address..."
              fullWidth
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  const email = e.target.value.trim();
                  if (email && !emailRecipients.includes(email)) {
                    setEmailRecipients(prev => [...prev, email]);
                    e.target.value = '';
                  }
                }
              }}
            />
            <Button
              variant="outlined"
              size="small"
              onClick={(e) => {
                const input = e.target.parentElement.querySelector('input');
                const email = input?.value?.trim();
                if (email && !emailRecipients.includes(email)) {
                  setEmailRecipients(prev => [...prev, email]);
                  input.value = '';
                }
              }}
            >
              Add
            </Button>
          </Box>

          {/* Recipients list */}
          <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
            {emailRecipients.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                No recipients added. Add email addresses above.
              </Typography>
            ) : (
              emailRecipients.map((email, index) => (
                <Chip
                  key={index}
                  label={email}
                  onDelete={() => handleRemoveRecipient(email)}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              ))
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSendDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSendQuotation}
            disabled={sending || emailRecipients.length === 0}
            startIcon={sending ? <CircularProgress size={20} /> : <SendIcon />}
          >
            {sending ? 'Sending...' : 'Send Quotation'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Quotation Dialog */}
      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Quotation</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Please provide a reason for rejecting this quotation.
          </Typography>
          <TextField
            label="Rejection Reason"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            fullWidth
            multiline
            rows={3}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleRejectQuotation}
            disabled={rejecting || !rejectReason.trim()}
            startIcon={rejecting && <CircularProgress size={20} />}
          >
            {rejecting ? 'Processing...' : 'Reject Quotation'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Convert Dialog */}
      <Dialog open={convertDialogOpen} onClose={() => setConvertDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Work Order</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            This will create a new work order from this quotation. All line items will be copied as tasks.
          </Typography>
          <Box sx={{ bgcolor: 'action.hover', p: 2, borderRadius: 1 }}>
            <Typography variant="subtitle2">Quotation: {quotationNumber}</Typography>
            <Typography variant="body2" color="text.secondary">{quotation.organization_name}</Typography>
            <Typography variant="body2" color="primary" fontWeight={600}>
              {formatCurrency(quotation.grand_total)}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConvertDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleConvert}
            disabled={converting}
            startIcon={converting ? <CircularProgress size={20} /> : <WorkOrderIcon />}
            sx={{
              backgroundColor: getBrandColor(theme),
              color: getBrandContrastText(theme),
              '&:hover': { backgroundColor: getBrandHover(theme) }
            }}
          >
            {converting ? 'Creating...' : 'Create Work Order'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Service Dialog */}
      <Dialog open={addServiceDialogOpen} onClose={() => setAddServiceDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Service</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Box display="flex" gap={1} mb={3}>
              <Button
                variant={!isAddingCustomService ? 'contained' : 'outlined'}
                onClick={() => setIsAddingCustomService(false)}
                fullWidth
              >
                From Catalog
              </Button>
              <Button
                variant={isAddingCustomService ? 'contained' : 'outlined'}
                onClick={() => setIsAddingCustomService(true)}
                fullWidth
              >
                Custom Service
              </Button>
            </Box>

            {!isAddingCustomService ? (
              <Autocomplete
                options={availableServices}
                getOptionLabel={(option) => `${option.name || ''}${option.type === 'INTERNAL' ? ' [Internal]' : ''}`}
                groupBy={(option) => option.type === 'INTERNAL' ? 'Internal Services' : 'Thaklis Services'}
                value={selectedService}
                onChange={(e, value) => setSelectedService(value)}
                renderInput={(params) => (
                  <TextField {...params} label="Select Service" placeholder="Search services..." />
                )}
                sx={{ mb: 2 }}
              />
            ) : (
              <>
                <TextField
                  label="Service Name"
                  value={customServiceName}
                  onChange={(e) => setCustomServiceName(e.target.value)}
                  fullWidth
                  sx={{ mb: 2 }}
                />
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Service Charge (Incl. Tax)"
                      type="number"
                      value={customServiceCharge}
                      onChange={(e) => setCustomServiceCharge(e.target.value)}
                      fullWidth
                      helperText="5% VAT included"
                      InputProps={{ startAdornment: <InputAdornment position="start">AED</InputAdornment> }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Government Charge"
                      type="number"
                      value={customGovtCharge}
                      onChange={(e) => setCustomGovtCharge(e.target.value)}
                      fullWidth
                      helperText="No VAT"
                      InputProps={{ startAdornment: <InputAdornment position="start">AED</InputAdornment> }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="Service Charge (Excl. Tax)"
                      value={customServiceCharge
                        ? Number((parseFloat(customServiceCharge) / 1.05).toFixed(2))
                        : ''}
                      disabled
                      fullWidth
                      InputProps={{ startAdornment: <InputAdornment position="start">AED</InputAdornment> }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="Tax (5% VAT)"
                      value={customServiceCharge
                        ? Number((parseFloat(customServiceCharge) - parseFloat(customServiceCharge) / 1.05).toFixed(2))
                        : ''}
                      disabled
                      fullWidth
                      InputProps={{ startAdornment: <InputAdornment position="start">AED</InputAdornment> }}
                    />
                  </Grid>
                </Grid>
              </>
            )}

            <TextField
              label="Markup (Incl. Tax)"
              type="number"
              value={addMarkup}
              onChange={(e) => setAddMarkup(e.target.value)}
              fullWidth
              placeholder="Optional"
              helperText="5% VAT will be extracted"
              InputProps={{ startAdornment: <InputAdornment position="start">AED</InputAdornment> }}
              sx={{ mt: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddServiceDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAddLineItem}
            disabled={
              addingLineItem ||
              (!isAddingCustomService
                ? !selectedService
                : !customServiceName || !customServiceCharge)
            }
          >
            {addingLineItem ? 'Adding...' : 'Add Service'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Line Item Markup Dialog — mirrors the invoice detail page flow.
          Input is markup INCLUDING tax; split client-side with VAT_RATE and
          sent to the backend as markupAmount + taxOnMarkup. */}
      <Dialog open={markupDialogOpen} onClose={closeLineItemMarkupDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <AddIcon color="success" />
            {selectedLineItem && parseFloat(selectedLineItem.markup_amount) > 0 ? 'Edit Markup' : 'Add Markup'}
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedLineItem && (
            <>
              <Box sx={{ bgcolor: 'action.hover', p: 2, borderRadius: 1, mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>{selectedLineItem.service_name}</Typography>
                {selectedLineItem.service_description && (
                  <Typography variant="body2" color="text.secondary">{selectedLineItem.service_description}</Typography>
                )}
              </Box>

              <Box sx={{ backgroundColor: '#f0fdf4', p: 2, borderRadius: 1, mb: 2 }}>
                {(() => {
                  const markupInclTax = parseFloat(lineItemMarkupAmount) || 0;
                  const markupExclTax = markupInclTax / (1 + VAT_RATE);
                  const taxOnMarkup = markupInclTax - markupExclTax;
                  const baseService = parseFloat(selectedLineItem.service_charge) || 0;
                  const baseTax = parseFloat(selectedLineItem.tax_value) || 0;
                  const newService = baseService + markupExclTax;
                  const newTax = baseTax + taxOnMarkup;
                  return (
                    <>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        SERVICE CHARGE
                      </Typography>
                      <Box display="flex" justifyContent="space-between" mb={0.5}>
                        <Typography variant="body2" color="text.secondary">Base:</Typography>
                        <Typography variant="body2">{formatCurrency(baseService)}</Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between" mb={1}>
                        <Typography variant="body2" color="text.secondary">+ Markup:</Typography>
                        <Typography variant="body2" color="success.main">+{formatCurrency(markupExclTax)}</Typography>
                      </Box>

                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        TAX ({VAT_RATE_DISPLAY})
                      </Typography>
                      <Box display="flex" justifyContent="space-between" mb={0.5}>
                        <Typography variant="body2" color="text.secondary">Base:</Typography>
                        <Typography variant="body2">{formatCurrency(baseTax)}</Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between" mb={1}>
                        <Typography variant="body2" color="text.secondary">+ Tax on Markup:</Typography>
                        <Typography variant="body2" color="success.main">+{formatCurrency(taxOnMarkup)}</Typography>
                      </Box>

                      <Divider sx={{ my: 1 }} />
                      <Box display="flex" justifyContent="space-between" mb={0.5}>
                        <Typography variant="body2" fontWeight={600}>Total Markup (incl. tax):</Typography>
                        <Typography variant="body2" fontWeight={600} color="success.main">
                          +{formatCurrency(markupInclTax)}
                        </Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">New Service Charge:</Typography>
                        <Typography variant="body2">{formatCurrency(newService)}</Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">New Tax:</Typography>
                        <Typography variant="body2">{formatCurrency(newTax)}</Typography>
                      </Box>
                    </>
                  );
                })()}
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Markup (Incl. Tax)"
                    value={lineItemMarkupAmount}
                    onChange={(e) => setLineItemMarkupAmount(e.target.value)}
                    fullWidth
                    type="number"
                    InputProps={{ startAdornment: <Typography sx={{ mr: 1 }}>AED</Typography> }}
                    helperText="Amount to add as markup on this line"
                    autoFocus
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Markup (Excl. Tax)"
                    value={lineItemMarkupAmount ? (parseFloat(lineItemMarkupAmount) / (1 + VAT_RATE)).toFixed(2) : ''}
                    fullWidth
                    disabled
                    InputProps={{ startAdornment: <Typography sx={{ mr: 1 }}>AED</Typography> }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Tax"
                    value={lineItemMarkupAmount
                      ? (parseFloat(lineItemMarkupAmount) - parseFloat(lineItemMarkupAmount) / (1 + VAT_RATE)).toFixed(2)
                      : ''}
                    fullWidth
                    disabled
                    InputProps={{ startAdornment: <Typography sx={{ mr: 1 }}>AED</Typography> }}
                  />
                </Grid>
              </Grid>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeLineItemMarkupDialog}>Cancel</Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleApplyLineItemMarkup}
            disabled={applyingLineItemMarkup || !lineItemMarkupAmount || parseFloat(lineItemMarkupAmount) <= 0}
            startIcon={applyingLineItemMarkup ? <CircularProgress size={20} /> : <AddIcon />}
          >
            {applyingLineItemMarkup ? 'Applying...' : 'Apply Markup'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reopen a FINAL quotation for editing (frontend-only gate) */}
      <Dialog open={reopenDialogOpen} onClose={() => setReopenDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reopen quotation for editing?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This quotation is finalized. Editing its line items will update a finalized
            document and may change the totals the customer sees. Continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setReopenDialogOpen(false)} color="inherit">Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={() => { setFinalEditMode(true); setReopenDialogOpen(false); }}
          >
            Reopen for editing
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default QuotationDetail;
